import { inject, Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { Widget } from "../../models/dashboard/widget.model";
import { DashboardActions } from "./dashboard.actions";
import { UserSettingsWidgetService } from "../../services/request/user-settings-widget.service";
import { ResetStateDefaults } from "../base-action-class.model";
import { CompoApp } from '../../services/CompoApp';
import { map, Observable, of, throwError } from 'rxjs';
import { RequestClientService } from '../../services/client/request-client.service';
import { delay } from 'rxjs/operators';


interface DashboardStateModel {
  userId: string;
  widgets: Widget[];
  isEditModeActive: boolean;
  initialized: boolean;
}

const getDefaults = (): DashboardStateModel => ({
  userId: null,
  widgets: null,
  isEditModeActive: false,
  initialized: false,
});


@State<DashboardStateModel>({
  name: 'dashboard',
  defaults: getDefaults()
})
@Injectable()
export class DashboardXsState {

  //region INJECTIONS
  private userSettingsWidgetService: UserSettingsWidgetService = inject(UserSettingsWidgetService);
  private app: CompoApp = inject(CompoApp);
  private requestClientService: RequestClientService = inject(RequestClientService);
  private store: Store = inject(Store);
  //endregion

  // region selector
  @Selector()
  static widgets(state: DashboardStateModel): Widget[] {
    return state.widgets;
  }

  @Selector()
  static isEditModeActive(state: DashboardStateModel): boolean {
    return state.isEditModeActive;
  }

  @Selector()
  static initialized(state: DashboardStateModel): boolean {
    return state.initialized;
  }
  // endregion

  // region actions
  @Action(DashboardActions.GetWidgets)
  getWidgets(ctx: StateContext<DashboardStateModel>): Observable<void> {
    const state: DashboardStateModel = ctx.getState();

    if (state.initialized && state.widgets?.length && state.userId === this.app.data.getUser().id) {
      return of();
    }

    const requestStartDate: Date = new Date();

    return this.userSettingsWidgetService.getWidgets()
      .pipe(
        delay(this.requestClientService.calcDefaultTimeoutOf(requestStartDate)),
        map((widgets: Widget[]) => {
          ctx.patchState({
            userId: this.app.data.getUser().id,
            initialized: true,
            widgets
          });
        })
      );
  }

  @Action(DashboardActions.EnterEditMode)
  public enterEditMode(ctx: StateContext<DashboardStateModel>): void {
    ctx.patchState({isEditModeActive: true});
  }

  @Action(DashboardActions.ExitEditMode)
  public exitEditMode(ctx: StateContext<DashboardStateModel>): void {
    ctx.patchState({isEditModeActive: false});
  }

  @Action(DashboardActions.SaveWidgets)
  public saveWidgets(
    ctx: StateContext<DashboardStateModel>,
    action: DashboardActions.SaveWidgets
  ): Observable<void> {
    if (!action.widgets) {
      return throwError(() => new Error("Widgets cannot be null!"));
    }

    const currentWidgetMap: Map<string, Widget>
      = ctx.getState().widgets.reduce((acc, widget) => acc.set(widget.id, widget), new Map());

    const changedWidgetIds: Set<string> = new Set();

    for (const widget of action.widgets) {
      const {cols, rows, x, y, minItemRows, ...widgetData} = widget;
      const currentWidget: Widget = currentWidgetMap.get(widget.id);
      if (currentWidget) {
        const {cols, rows, x, y, minItemRows, ...currentWidgetData} = currentWidget;
        if (JSON.stringify(widgetData) !== JSON.stringify(currentWidgetData)) {
          changedWidgetIds.add(widget.id);
        }
      }
    }

    return this.userSettingsWidgetService.saveWidgetGrid(action.widgets)
      .pipe(
        map((widgets: Widget[]) => {
          ctx.patchState({
            widgets
          });
          this.store.dispatch(new DashboardActions.SaveWidgetsSuccessful(changedWidgetIds));
        })
      );
  }

  @Action(ResetStateDefaults)
  public resetState(ctx: StateContext<DashboardStateModel>): void {
    ctx.patchState({...getDefaults()});
  }
  // endregion
}
